/**
 * A conversa com o backend.
 *
 * A autenticação é da Etapa 9: até lá a usuária vem de um cabeçalho, guardado
 * no navegador e trocável por `?usuaria=` na URL. A API recusa isso em
 * produção, então esta gambiarra não sai daqui.
 */

export type ItemDoDia = {
  nome: string
  hora: string | null
  feito: boolean
  id: string | null
}

export type Bloco = {
  /** De qual bloco esta linha é. Sem ele, a tela Hoje não deixa consertar nada. */
  id: string | null
  tipo: string
  inicio: string
  fim: string
  protegido: boolean
  descricao: string | null
}

export type Dose = {
  /** A dose por trás do cartão. Sem ela o "Dei" não tem o que mandar. */
  id: string | null
  remedio: string
  para_quem: string
  /** A quantidade em texto, como está na receita: "5 ml", "1 comprimido". */
  dose: string
  hora: string
  ordem: number
  total: number
  dada: boolean
}

export type Compromisso = {
  /** Qual compromisso é. Sem ele a tela Hoje não deixa consertar o feriado. */
  id: string | null
  /** Se ela já disse que foi, **neste dia**. Numa série, cada data por si. */
  cumprido: boolean
  titulo: string
  quando: string
  hora: string
  local: string | null
}

export type Dia = {
  nome_tratamento: string
  hoje: string
  agora: string | null
  plano: ItemDoDia[]
  rotina: ItemDoDia[]
  blocos: Bloco[]
  doses: Dose[]
  compromissos: Compromisso[]
  compras: string[]
  frase_do_dia: string | null
  confirmacao_pendente: string | null
}

export type RespostaDaRuth = {
  resposta: string
  intencao: string | null
  gravadas: string[]
  precisa_confirmacao: boolean
}

export type ItemDaSemana = {
  tipo: 'tarefa' | 'protegido' | 'compromisso'
  /**
   * De qual coisa esta linha é: a ocorrência da tarefa, o bloco, o compromisso.
   *
   * Sem isto a tela desenha sete dias que a mãe não pode tocar, e o conserto do
   * que ela está vendo passa a morar noutra tela — que é a mesma coisa que não
   * ter conserto (regra 7).
   */
  id: string
  nome: string
  hora: string
  /** Só bloco tem fim: ele ocupa um intervalo, a tarefa acontece numa hora. */
  fim: string | null
  /** Só tarefa: a ocorrência é o dia dela, a tarefa é o que sai do plano. */
  tarefa_id: string | null
  /**
   * Se a mãe marcou que fez, ou que foi, na data desta linha.
   *
   * Não é boletim: a grade mostra o que ela mesma marcou, e a ausência de marca
   * não vira nada — não existe "atrasado", não existe "não fez", ninguém soma.
   */
  cumprido: boolean
}

export type DiaDaSemana = {
  data: string
  itens: ItemDaSemana[]
}

export type Semana = {
  inicio: string
  fim: string
  hoje: string
  tarefas: number
  resumo: string
  dias: DiaDaSemana[]
  /**
   * O dia dela está cheio de ponta a ponta e nada da casa coube.
   *
   * Campo, e não comparação de texto: o `resumo` é copy, e bastaria alguém
   * trocar uma vírgula para o botão de conserto sumir calado — sumiria
   * justamente na casa onde nada coube. Quem contou os buracos do dia foi o
   * servidor, então é ele quem diz que não havia nenhum.
   */
  sem_espaco: boolean
  /**
   * A semana inteira já acabou.
   *
   * A tela usa para não oferecer conserto do que não tem mais conserto: ela
   * voltou ali para ver o que aconteceu, não para consertar o que já foi.
   */
  passada: boolean
}

/* ------------------------------------------------------------- remédios */

export type StatusDaDose = 'dada' | 'pendente' | 'futura'

export type DoseDeHoje = {
  id: string
  hora: string
  status: StatusDaDose
}

export type Remedio = {
  id: string
  nome: string
  para_quem: string
  /** A quantidade em texto, como na receita: "5 ml", "2 gotas". */
  dose: string
  /** "de 8 em 8 h" — nulo em uso contínuo de uma vez ao dia. */
  intervalo_texto: string | null
  continuo: boolean
  termina_em: string | null
  /** Contar dose é cuidado, e é a única barra de progresso do produto. */
  dose_atual: number | null
  total: number | null
  doses_de_hoje: DoseDeHoje[]
  proxima_dose_id: string | null
  receita: Receita | null

  /* O que o formulário de correção abre preenchido. */
  concentracao: string | null
  via: string | null
  observacoes: string | null
  /** De quantas em quantas horas, como está na receita. */
  intervalo_horas: number
  /** Nulo é uso contínuo: todo dia, sem prazo. */
  duracao_dias: number | null
  /** A primeira dose por vir, na hora da parede da casa. Nulo se acabou. */
  proxima_dose_em: string | null
}

/**
 * O que a mãe pode corrigir num remédio. Campo ausente não muda nada.
 *
 * Corrigir aqui é consertar o que a Ruth entendeu, não mexer no que o médico
 * escreveu. Receita nova é foto nova.
 */
export type CorrecaoDeRemedio = {
  nome?: string
  concentracao?: string | null
  dose?: string
  via?: string | null
  observacoes?: string | null
  intervalo_horas?: number
  duracao_dias?: number | null
  /** "2026-09-06T21:00" — hora da casa, sem fuso. Quem ancora é o servidor. */
  proxima_dose?: string
}

/**
 * Um esquema de remédio que a mãe monta na tela.
 *
 * Nada tem padrão — nem dose, nem intervalo, nem duração. Um padrão aqui é
 * exatamente a inferência que a regra 1 proíbe: quem escreve cada campo é ela,
 * olhando a receita. `confirmo` é o "sim" dela na frase que repete tudo, e sem
 * ele o banco recusa o esquema.
 */
export type RemedioNovo = {
  nome: string
  para_quem: string
  dose: string
  intervalo_horas: number
  concentracao?: string | null
  via?: string | null
  observacoes?: string | null
  duracao_dias?: number | null
  primeira_dose: string
  confirmo: boolean
}

/** O que mudou, em número, para a tela contar à mãe o que aconteceu. */
export type RemedioCorrigido = {
  id: string
  nome: string
  dose: string
  intervalo_texto: string | null
  doses_mantidas: number
  doses_refeitas: number
  total: number
}

export type Receita = {
  id: string
  medico: string | null
  data: string | null
  /** Se existe foto guardada. A imagem em si nunca vem no JSON. */
  tem_foto: boolean
}

export type Remedios = {
  remedios: Remedio[]
}

/* -------------------------------------------------------------- compras */

export type ItemDeCompra = {
  id: string
  nome: string
  quantidade: string | null
}

/**
 * O que a mãe pode corrigir num item da lista. Campo ausente fica como está.
 *
 * `tirar_a_quantidade` pelo mesmo motivo do `tirar_a_hora` da casa: `null` não
 * se distingue de "não mexi nisso".
 */
export type CorrecaoDeItem = {
  nome?: string
  quantidade?: string
  tirar_a_quantidade?: boolean
}

export type Compras = {
  itens: ItemDeCompra[]
  /** Comprados nas últimas horas: dá para desfazer sem conversar. */
  comprados: ItemDeCompra[]
}

/* --------------------------------------------------------- compromissos */

export type CompromissoMarcado = {
  id: string
  titulo: string
  para_quem: string | null
  quando: string
  hora: string
  fim: string | null
  local: string | null
  lembrete_vespera: boolean
  lembrete_dia: boolean
  /** "toda terça e quinta", quando se repete. Vazio é compromisso de uma vez. */
  repete: string
  /** Os mesmos dias em código, para o formulário marcar as caixas certas. */
  repete_dias: string[]
  /** Se ela já disse que foi, na data desta linha. */
  cumprido: boolean
}

/**
 * O que a mãe pode corrigir num compromisso.
 *
 * Campo ausente não muda nada; `null` apaga o que é opcional. É a mesma
 * distinção que a rota faz, e ela existe para uma tela que manda só o que
 * mudou não apagar o que a mãe nem tocou.
 */
/**
 * O que a mãe pode corrigir numa coisa da casa. Campo ausente fica como está.
 *
 * `tirar_a_hora` existe porque `hora: null` não dá para distinguir de "não
 * mexi nisso": sem ele, uma tarefa que ganhou hora nunca mais conseguiria
 * voltar a não ter hora — e tarefa sem hora é a que não vira alarme no dia
 * dela (regra 4).
 */
export type CorrecaoDeTarefa = {
  ativo?: boolean
  nome?: string
  frequencia?: Frequencia
  vezes_por_periodo?: number
  dias?: string[]
  hora?: string
  tirar_a_hora?: boolean
}

/** Um compromisso que a mãe marca na tela, quando falar não dá. */
export type CompromissoNovo = {
  titulo: string
  quando: string
  hora: string
  fim?: string | null
  local?: string | null
  para_quem?: string | null
  repete_dias?: string[]
}

export type CorrecaoDeCompromisso = {
  titulo?: string
  quando?: string
  hora?: string
  fim?: string | null
  local?: string | null
  repete_dias?: string[]
}

export type Compromissos = {
  /** O dia de hoje na casa. O rótulo "Hoje" não sai do relógio do aparelho. */
  hoje: string
  compromissos: CompromissoMarcado[]
}

/* -------------------------------------------------------- configurações */

export type MembroDaCasa = {
  id: string
  nome: string
  papel: string
  /** Como a mãe chama a pessoa: "sogra", "avó do Theo". Quando existe, manda. */
  parentesco: string | null
  data_nascimento: string | null
}

/** Os cinco papéis que o sistema entende. O resto é `parentesco`, em texto. */
export type PapelDeMembro = 'mae' | 'pai' | 'crianca' | 'ajudante' | 'outro'

/**
 * Alguém que a Ruth não conseguiu pôr na casa.
 *
 * Idade em anos, ou em meses se for bebê — é como a mãe fala, e é a mesma conta
 * que a primeira conversa faz.
 */
export type PessoaNova = {
  nome: string
  papel: PapelDeMembro
  parentesco?: string | null
  idade_anos?: number | null
  idade_meses?: number | null
}

/** O que ela pode consertar em quem já está. Campo ausente fica como está. */
export type CorrecaoDePessoa = Partial<PessoaNova>

export type TipoDeBloco =
  | 'trabalho'
  | 'almoco'
  | 'escola_levar'
  | 'escola_buscar'
  | 'sono_crianca'
  | 'mamada'
  | 'tempo_com_filhos'
  | 'outro'

/** Uma parte fixa do dia. O planner encaixa tarefa nos buracos entre elas. */
export type BlocoDoDia = {
  id: string
  tipo: TipoDeBloco
  descricao: string | null
  dias: string[]
  inicio: string
  fim: string
  /** Regra 3: nem tarefa nem aviso entram num bloco protegido. */
  protegido: boolean
}

export type BlocoNovo = {
  tipo: TipoDeBloco
  descricao?: string | null
  dias: string[]
  inicio: string
  fim: string
  protegido?: boolean
}

export type CorrecaoDeBloco = Partial<BlocoNovo>

export type ComodoDaCasa = {
  id: string
  tipo: TipoDeComodo
  nome: string
}

export type TipoDeComodo =
  | 'cozinha'
  | 'banheiro'
  | 'quarto'
  | 'sala'
  | 'lavanderia'
  | 'quintal'
  | 'varanda'
  | 'area_servico'
  | 'escritorio'
  | 'outro'

/** Um cômodo que a primeira conversa não ouviu, ou o conserto de um que ouviu. */
export type ComodoNovo = { tipo: TipoDeComodo; nome: string }
export type CorrecaoDeComodo = Partial<ComodoNovo>

export type Configuracoes = {
  nome_tratamento: string
  timezone: string
  resumo_hora: string
  silencio_inicio: string
  fechamento_noturno: boolean
  acorda: string
  dorme: string
  tem_ajudante: boolean
  dias_ajudante: string[]
  capacidade_por_dia: Record<string, string>
  membros: MembroDaCasa[]
  comodos: ComodoDaCasa[]
  blocos: BlocoDoDia[]
  consentimento_lgpd_em: string | null
}

export type MudancaNasConfiguracoes = Partial<
  Pick<
    Configuracoes,
    | 'nome_tratamento'
    | 'resumo_hora'
    | 'silencio_inicio'
    | 'fechamento_noturno'
    | 'acorda'
    | 'dorme'
    | 'tem_ajudante'
    | 'dias_ajudante'
    | 'capacidade_por_dia'
  >
>

/* --------------------------------------------------------- onboarding */

export type PassoDoOnboarding = {
  concluido: boolean
  numero: number
  total: number
  titulo: string
  mensagens: MensagemDaConversa[]
  /**
   * Vazio na maioria dos passos. Quando vem preenchido, a pergunta se responde
   * tocando num deles, e o rótulo vai como `texto` para `/responder`.
   */
  botoes: string[]
}

/* -------------------------------------------------------- integrações */

export type Integracao = {
  chave: string
  nome: string
  ligada: boolean
  /** O que deixa de funcionar sem ela, em português de gente. */
  sem_ela: string
}

export type Integracoes = {
  integracoes: Integracao[]
}

/* -------------------------------------------------- avisos no celular */

/** A pública do par VAPID. A privada não tem rota, e é de propósito. */
export type ChaveDePush = {
  chave: string
}

/** O `PushSubscription.toJSON()` do navegador, do jeito que ele sai de lá. */
export type InscricaoDePush = {
  endpoint: string
  keys: { p256dh: string; auth: string }
}

export type GrupoDaCasa = 'dia' | 'semana' | 'mes'

/** Toda cadência que o plano conhece. `segue` é "depois de outra tarefa". */
export type Frequencia =
  | 'diaria'
  | 'semanal'
  | 'quinzenal'
  | 'mensal'
  | 'trimestral'
  | 'semestral'
  | 'anual'
  | 'segue'

export type TarefaDaCasa = {
  id: string
  nome: string
  grupo: GrupoDaCasa
  /**
   * A cadência crua. `grupo` é para agrupar e `cadencia` é para ler; nenhum
   * dos dois volta a ser frequência sem chute, e a tela não chuta.
   */
  frequencia: Frequencia
  ativo: boolean
  vezes: number
  /** "de 15 em 15 dias" — só quando a cadência não cabe no nome da aba. */
  cadencia: string | null
  dias: string[]
  /** Só quando a mãe disse a hora; o horário do planner não vale como promessa. */
  hora: string | null
  de_quem: string | null
  marcador: string | null
}

/** Uma coisa da casa que a mãe põe no plano. */
export type TarefaDaCasaNova = {
  nome: string
  frequencia?: Frequencia
  vezes_por_periodo?: number
  dias?: string[]
  /** Só quando ela disser a hora: sem hora, a tarefa não vira mais um alarme. */
  hora?: string | null
}

export type Casa = {
  tarefas: TarefaDaCasa[]
}

export type TipoDeMensagem = 'texto' | 'audio' | 'imagem'

export type MensagemDaConversa = {
  id: string
  de: 'mae' | 'ruth'
  tipo: TipoDeMensagem
  texto: string | null
  /** ISO com o fuso da casa: "2026-09-04T13:42:07-03:00". */
  quando: string
  espera_confirmacao: boolean
}

export type Conversa = {
  mensagens: MensagemDaConversa[]
}

const CHAVE_DA_USUARIA = 'ruth.usuaria'
const CHAVE_DA_SESSAO = 'ruth.sessao'

/**
 * A sessão do link mágico. É o caminho de verdade — o `?usuaria=` só existe
 * para o desenvolvimento e para a casa demo, e o servidor recusa esse atalho
 * em produção.
 */
export function sessaoAtual(): string | null {
  return localStorage.getItem(CHAVE_DA_SESSAO)
}

export function guardarSessao(sessao: string): void {
  localStorage.setItem(CHAVE_DA_SESSAO, sessao)
}

export function esquecerSessao(): void {
  localStorage.removeItem(CHAVE_DA_SESSAO)
  localStorage.removeItem(CHAVE_DA_USUARIA)
}

/** O cabeçalho que identifica quem está falando. A sessão vem primeiro. */
function quemSou(): Record<string, string> {
  const sessao = sessaoAtual()
  if (sessao) return { Authorization: `Bearer ${sessao}` }
  const usuaria = usuariaAtual()
  return usuaria ? { 'X-Ruth-Usuario': usuaria } : {}
}

export function usuariaAtual(): string | null {
  const daUrl = new URLSearchParams(window.location.search).get('usuaria')
  if (daUrl) {
    localStorage.setItem(CHAVE_DA_USUARIA, daUrl)
    return daUrl
  }
  return localStorage.getItem(CHAVE_DA_USUARIA)
}

export class ErroDaApi extends Error {
  constructor(
    readonly status: number,
    mensagem: string,
  ) {
    super(mensagem)
  }
}

/**
 * A frase que a mãe lê quando não deu.
 *
 * Só a do servidor passa: ela vem escrita para gente ("não achei esse bloco").
 * O `Failed to fetch` do elevador sem sinal e o `HTTP 502` do proxy são recado
 * de máquina para máquina, e a mãe que lê isso acha que quebrou alguma coisa.
 *
 * Mora aqui, e não numa tela, porque toda tela precisa: a Semana mostrava a
 * mensagem crua e ninguém tinha percebido.
 */
export function fraseDoErro(erro: unknown, padrao: string): string {
  if (erro instanceof ErroDaApi && !erro.message.startsWith('HTTP ')) return erro.message
  return padrao
}

async function chamar<T>(caminho: string, opcoes: RequestInit = {}): Promise<T> {
  const resposta = await fetch(`/api${caminho}`, {
    ...opcoes,
    headers: {
      'Content-Type': 'application/json',
      ...quemSou(),
      ...opcoes.headers,
    },
  })

  if (!resposta.ok) {
    const corpo = await resposta.json().catch(() => ({}))
    throw new ErroDaApi(resposta.status, corpo.detail ?? `HTTP ${resposta.status}`)
  }
  return resposta.json() as Promise<T>
}

/**
 * Arquivo vai como multipart, e o `Content-Type` é o navegador que escreve:
 * ele precisa carregar a fronteira do corpo, que só ele conhece.
 */
async function enviarArquivo<T>(caminho: string, campo: string, arquivo: Blob, nome: string) {
  const corpo = new FormData()
  corpo.append(campo, arquivo, nome)
  corpo.append('tipo', arquivo.type || 'application/octet-stream')

  const resposta = await fetch(`/api${caminho}`, {
    method: 'POST',
    body: corpo,
    headers: quemSou(),
  })

  if (!resposta.ok) {
    const erro = await resposta.json().catch(() => ({}))
    throw new ErroDaApi(resposta.status, erro.detail ?? `HTTP ${resposta.status}`)
  }
  return resposta.json() as Promise<T>
}

export const api = {
  /**
   * O dia da mãe: o de hoje, ou outro que ela pediu.
   *
   * "Posso querer ver o histórico, a agenda nos dias anteriores." Dois meses
   * para trás e duas semanas para frente; fora disso o servidor recusa com
   * uma frase.
   */
  hoje: (dia?: string) => chamar<Dia>(`/hoje${dia ? `?dia=${dia}` : ''}`),

  conversa: (limite = 40) => chamar<Conversa>(`/conversa?limite=${limite}`),

  /**
   * A semana de hoje, ou outra. `de` é qualquer dia dela — a tela manda a
   * segunda, mas a quinta daria a mesma semana.
   */
  semana: (de?: string) => chamar<Semana>(`/semana${de ? `?de=${de}` : ''}`),

  casa: () => chamar<Casa>('/casa'),

  remedios: () => chamar<Remedios>('/remedios'),

  dei: (dose: string) =>
    chamar<{ status: string }>(`/remedios/doses/${dose}/dada`, { method: 'POST' }),

  /**
   * A frase que ela lê antes de o esquema existir.
   *
   * Vem do servidor, e não da tela, porque é a mesma frase que a conversa
   * monta, do mesmo código. Uma segunda cópia aqui divergiria — e o que
   * diverge numa frase dessas é a dose que uma criança vai tomar.
   */
  conferirRemedio: (remedio: RemedioNovo) =>
    chamar<{ frase: string }>('/remedios/conferir', {
      method: 'POST',
      body: JSON.stringify(remedio),
    }),

  montarRemedio: (remedio: RemedioNovo) =>
    chamar<RemedioCorrigido>('/remedios', {
      method: 'POST',
      body: JSON.stringify(remedio),
    }),

  /** O tratamento acabou. O que ela já deu fica; o que vinha, para. */
  encerrarRemedio: (remedio: string) =>
    chamar<RemedioCorrigido>(`/remedios/${remedio}`, { method: 'DELETE' }),

  corrigirRemedio: (remedio: string, campos: CorrecaoDeRemedio) =>
    chamar<RemedioCorrigido>(`/remedios/${remedio}`, {
      method: 'PATCH',
      body: JSON.stringify(campos),
    }),

  compras: () => chamar<Compras>('/compras'),

  /** "leit" digitado com o dedo errado não precisa mais esperar o mercado. */
  corrigirCompra: (item: string, campos: CorrecaoDeItem) =>
    chamar<ItemDeCompra>(`/compras/${item}`, {
      method: 'PATCH',
      body: JSON.stringify(campos),
    }),

  /**
   * Tira da lista o que nunca deveria ter entrado.
   *
   * Não é "comprado": comprado é uma coisa que aconteceu, e a Ruth guarda por
   * umas horas para ela poder desfazer. Isto é a linha não devendo existir.
   */
  apagarCompra: (item: string) =>
    chamar<{ status: string }>(`/compras/${item}`, { method: 'DELETE' }),

  acrescentarCompra: (nome: string, quantidade: string | null = null) =>
    chamar<ItemDeCompra>('/compras', {
      method: 'POST',
      body: JSON.stringify({ nome, quantidade }),
    }),

  comprar: (item: string) =>
    chamar<{ status: string }>(`/compras/${item}/comprado`, { method: 'POST' }),

  devolver: (item: string) =>
    chamar<{ status: string }>(`/compras/${item}/voltar`, { method: 'POST' }),

  compromissos: () => chamar<Compromissos>('/compromissos'),

  /** O que a mãe corrigiu. Só o que ela mexeu vai — o resto fica como está. */
  marcarCompromisso: (compromisso: CompromissoNovo) =>
    chamar<CompromissoMarcado>('/compromissos', {
      method: 'POST',
      body: JSON.stringify(compromisso),
    }),

  corrigirCompromisso: (compromisso: string, campos: CorrecaoDeCompromisso) =>
    chamar<CompromissoMarcado>(`/compromissos/${compromisso}`, {
      method: 'PATCH',
      body: JSON.stringify(campos),
    }),

  /**
   * "Amanhã é feriado, não tem aula." Tira um dia da série sem desmarcá-la.
   *
   * O dia fica guardado em vez de a ocorrência ser apagada, e é isso que faz
   * `voltarODia` existir: ela tira o feriado e pode devolver.
   */
  pularODia: (compromisso: string, dia: string) =>
    chamar<CompromissoMarcado>(`/compromissos/${compromisso}/pular`, {
      method: 'POST',
      body: JSON.stringify({ dia }),
    }),

  /**
   * "Fui." Por data, e não por compromisso: numa série, ter ido na terça não
   * diz nada sobre a quinta — é o mesmo desenho de `pularODia`.
   */
  compromissoCumprido: (compromisso: string, dia: string) =>
    chamar<CompromissoMarcado>(`/compromissos/${compromisso}/cumprido`, {
      method: 'POST',
      body: JSON.stringify({ dia }),
    }),

  /** Tocou sem querer, ou mudou de ideia. Desfazer é parte de poder marcar. */
  compromissoDescumprido: (compromisso: string, dia: string) =>
    chamar<CompromissoMarcado>(`/compromissos/${compromisso}/descumprido`, {
      method: 'POST',
      body: JSON.stringify({ dia }),
    }),

  voltarODia: (compromisso: string, dia: string) =>
    chamar<CompromissoMarcado>(`/compromissos/${compromisso}/voltar`, {
      method: 'POST',
      body: JSON.stringify({ dia }),
    }),

  cancelarCompromisso: (compromisso: string) =>
    chamar<{ status: string }>(`/compromissos/${compromisso}/cancelar`, { method: 'POST' }),

  configuracoes: () => chamar<Configuracoes>('/configuracoes'),

  mudarConfiguracoes: (mudanca: MudancaNasConfiguracoes) =>
    chamar<Configuracoes>('/configuracoes', {
      method: 'PATCH',
      body: JSON.stringify(mudanca),
    }),

  acrescentarPessoa: (pessoa: PessoaNova) =>
    chamar<Configuracoes>('/configuracoes/membros', {
      method: 'POST',
      body: JSON.stringify(pessoa),
    }),

  corrigirPessoa: (membro: string, campos: CorrecaoDePessoa) =>
    chamar<Configuracoes>(`/configuracoes/membros/${membro}`, {
      method: 'PATCH',
      body: JSON.stringify(campos),
    }),

  acrescentarBloco: (bloco: BlocoNovo) =>
    chamar<Configuracoes>('/configuracoes/blocos', {
      method: 'POST',
      body: JSON.stringify(bloco),
    }),

  corrigirBloco: (bloco: string, campos: CorrecaoDeBloco) =>
    chamar<Configuracoes>(`/configuracoes/blocos/${bloco}`, {
      method: 'PATCH',
      body: JSON.stringify(campos),
    }),

  tirarBloco: (bloco: string) =>
    chamar<Configuracoes>(`/configuracoes/blocos/${bloco}`, { method: 'DELETE' }),

  acrescentarComodo: (comodo: ComodoNovo) =>
    chamar<Configuracoes>('/configuracoes/comodos', {
      method: 'POST',
      body: JSON.stringify(comodo),
    }),

  corrigirComodo: (comodo: string, campos: CorrecaoDeComodo) =>
    chamar<Configuracoes>(`/configuracoes/comodos/${comodo}`, {
      method: 'PATCH',
      body: JSON.stringify(campos),
    }),

  tirarComodo: (comodo: string) =>
    chamar<Configuracoes>(`/configuracoes/comodos/${comodo}`, { method: 'DELETE' }),

  onboarding: () => chamar<PassoDoOnboarding>('/onboarding'),

  responderOnboarding: (texto: string) =>
    chamar<PassoDoOnboarding>('/onboarding/responder', {
      method: 'POST',
      body: JSON.stringify({ texto }),
    }),

  responderFalando: (audio: Blob, nome = 'fala.webm') =>
    enviarArquivo<PassoDoOnboarding>('/onboarding/audio', 'audio', audio, nome),

  /** Só olha onde a conversa parou, sem começar nada. */
  onboardingEstado: () => chamar<PassoDoOnboarding>('/onboarding/estado'),

  integracoes: () => chamar<Integracoes>('/integracoes'),

  criarTarefaDaCasa: (tarefa: TarefaDaCasaNova) =>
    chamar<TarefaDaCasa>('/casa/tarefas', {
      method: 'POST',
      body: JSON.stringify(tarefa),
    }),

  /** Tira do plano de vez. Desligar guarda; isto é para o que nunca foi dela. */
  tirarTarefaDaCasa: (tarefa: string) =>
    chamar<{ status: string }>(`/casa/tarefas/${tarefa}`, { method: 'DELETE' }),

  /**
   * Corrigir uma coisa da casa. Campo ausente não muda nada.
   *
   * Existe porque a linha só sabia ser ligada, desligada ou apagada: "lavar o
   * banheiro toda semana" não virava "a cada quinze dias", e o único caminho
   * era apagar e criar de novo — perdendo junto o histórico do que ela já fez.
   */
  corrigirTarefaDaCasa: (tarefa: string, campos: CorrecaoDeTarefa) =>
    chamar<TarefaDaCasa>(`/casa/tarefas/${tarefa}`, {
      method: 'PATCH',
      body: JSON.stringify(campos),
    }),

  virarAChave: (tarefa: string, ativo: boolean) =>
    chamar<TarefaDaCasa>(`/casa/tarefas/${tarefa}`, {
      method: 'PATCH',
      body: JSON.stringify({ ativo }),
    }),

  falar: (texto: string) =>
    chamar<RespostaDaRuth>('/chat', { method: 'POST', body: JSON.stringify({ texto }) }),

  mandarFoto: (foto: Blob, nome = 'receita.jpg') =>
    enviarArquivo<RespostaDaRuth>('/chat/foto', 'foto', foto, nome),

  mandarAudio: (audio: Blob, nome = 'fala.webm') =>
    enviarArquivo<RespostaDaRuth>('/chat/audio', 'audio', audio, nome),

  marcarFeito: (ocorrencia: string) =>
    chamar<{ status: string }>(`/ocorrencias/${ocorrencia}/feito`, { method: 'POST' }),

  /**
   * Tocou sem querer. Desfazer é parte de poder marcar.
   *
   * O compromisso já sabia voltar atrás e a tarefa não: a mesma bolinha era
   * desfazível de um lado e definitiva do outro.
   */
  desmarcarFeito: (ocorrencia: string) =>
    chamar<{ status: string }>(`/ocorrencias/${ocorrencia}/voltar`, { method: 'POST' }),

  maisTarde: (ocorrencia: string, minutos = 60) =>
    chamar<{ status: string }>(`/ocorrencias/${ocorrencia}/mais-tarde`, {
      method: 'POST',
      body: JSON.stringify({ minutos }),
    }),

  /** 503 quando a instalação ainda não tem par VAPID. */
  chavePublicaDePush: () => chamar<ChaveDePush>('/push/chave-publica'),

  /** Idempotente por endereço: reinscrever o mesmo aparelho atualiza. */
  inscreverNosAvisos: (inscricao: InscricaoDePush) =>
    chamar<{ status: string }>('/push/inscrever', {
      method: 'POST',
      body: JSON.stringify(inscricao),
    }),

  sairDosAvisos: (endpoint: string) =>
    chamar<{ status: string }>('/push/inscrever', {
      method: 'DELETE',
      body: JSON.stringify({ endpoint }),
    }),
}

/** "09:00:00" vira "9h"; "15:30:00" vira "15h30". Como a Ruth escreve. */
export function hhmm(hora: string | null): string {
  if (!hora) return ''
  const [h, m] = hora.split(':')
  const minuto = Number(m)
  return minuto ? `${Number(h)}h${m}` : `${Number(h)}h`
}

const DIAS = [
  'Domingo',
  'Segunda',
  'Terça',
  'Quarta',
  'Quinta',
  'Sexta',
  'Sábado',
]
const MESES = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
]

/** "2026-09-04" vira "Quinta, 4 de setembro". */
export function dataPorExtenso(iso: string): string {
  const [ano, mes, dia] = iso.split('-').map(Number)
  const data = new Date(ano, mes - 1, dia)
  return `${DIAS[data.getDay()]}, ${dia} de ${MESES[mes - 1]}`
}

/**
 * O dia e a hora saem do texto do ISO, não de `new Date`.
 *
 * O backend já manda tudo no fuso da casa. Passar por `Date` converteria para
 * o fuso do aparelho — e uma mãe viajando veria a conversa de ontem marcada
 * como hoje.
 */
export function diaDaCasa(iso: string): string {
  return iso.slice(0, 10)
}

export function horaDaCasa(iso: string): string {
  return hhmm(iso.slice(11, 19))
}

/** "2026-08-31" e "2026-09-06" viram "31 de agosto a 6 de setembro". */
export function intervaloPorExtenso(inicio: string, fim: string): string {
  const [, mesA, diaA] = inicio.split('-').map(Number)
  const [, mesB, diaB] = fim.split('-').map(Number)
  if (mesA === mesB) return `${diaA} a ${diaB} de ${MESES[mesA - 1]}`
  return `${diaA} de ${MESES[mesA - 1]} a ${diaB} de ${MESES[mesB - 1]}`
}

/**
 * O mesmo dia, tantos dias para frente ou para trás, em texto ISO.
 *
 * `Date.UTC` de propósito: a data vem do servidor no fuso da casa, e somar dias
 * pelo relógio do aparelho move o dia inteiro em quem estiver noutro fuso.
 *
 * Mora aqui porque duas telas andam no tempo — a Semana e a Hoje — e duas
 * cópias da mesma aritmética de data são duas cópias que divergem no dia em que
 * alguém consertar uma.
 */
export function deslocar(dia: string, dias: number): string {
  const [ano, mes, data] = dia.split('-').map(Number)
  const movido = new Date(Date.UTC(ano, mes - 1, data + dias))
  return movido.toISOString().slice(0, 10)
}

const DIAS_CURTOS = ['SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SÁB', 'DOM']

/** "2026-08-31" vira ["SEG", 31]. Sem `Date`: o fuso é o da casa. */
export function diaDaSemana(iso: string): [string, number] {
  const [ano, mes, dia] = iso.split('-').map(Number)
  const semana = new Date(Date.UTC(ano, mes - 1, dia)).getUTCDay()
  return [DIAS_CURTOS[(semana + 6) % 7], dia]
}

/** A saudação segue a hora da casa, não a do aparelho. */
export function saudacao(agora: string | null): string {
  const hora = agora ? Number(agora.split(':')[0]) : new Date().getHours()
  if (hora < 12) return 'Bom dia'
  if (hora < 18) return 'Boa tarde'
  return 'Boa noite'
}

/**
 * A foto da receita, para a mãe mostrar na farmácia.
 *
 * Vem por `fetch` e vira um endereço de blob em vez de ir direto num `src`:
 * a rota exige identidade, e `<img src>` não carrega cabeçalho. Pôr a
 * identidade na URL resolveria e seria pior — ela ficaria no histórico do
 * navegador, no log do servidor e no referer.
 *
 * Quem chama é dono do endereço devolvido e precisa soltá-lo com
 * `URL.revokeObjectURL` ao fechar, senão a imagem fica na memória da aba.
 */
export async function fotoDaReceita(receitaId: string): Promise<string> {
  const resposta = await fetch(`/api/remedios/receitas/${receitaId}/foto`, {
    headers: { ...quemSou() },
  })
  if (!resposta.ok) {
    throw new ErroDaApi(resposta.status, 'Não achei essa receita guardada.')
  }
  return URL.createObjectURL(await resposta.blob())
}
